import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, autoRetried: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Auto-recuperación: la mayoría de estos errores son por una versión vieja
    // en caché tras un despliegue. Recargar una vez suele arreglarlo.
    if (!this.state.autoRetried && !this.props.fallbackTitle) {
      this.setState({ autoRetried: true });
      setTimeout(() => {
        try {
          if ('caches' in window) {
            caches.keys().then((names) => Promise.all(names.map((n) => caches.delete(n)))).catch(() => {});
          }
        } finally {
          window.location.reload();
        }
      }, 1200);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, autoRetried: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const scoped = Boolean(this.props.fallbackTitle);
      // Si es auto-reintento, mostrar pantalla de carga amable en vez de error
      if (!scoped && this.state.autoRetried) {
        return (
          <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col items-center justify-center p-6">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
            </div>
            <h1 className="text-xl font-extrabold text-stone-900 mb-2">Actualizando…</h1>
            <p className="text-stone-500 text-sm text-center max-w-xs">
              Estamos cargando la versión más reciente, un momento.
            </p>
          </div>
        );
      }
      const title = this.props.fallbackTitle || 'Algo no salió bien';
      const message = this.props.fallbackMessage || 'Toca reintentar para continuar.';
      return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col items-center justify-center p-6" data-testid="error-boundary-fallback">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-6">
            <span className="text-4xl">🛣️</span>
          </div>
          <h1 className="text-2xl font-extrabold text-stone-900 mb-2 text-center" data-testid="error-boundary-title">
            {title}
          </h1>
          <p className="text-stone-500 text-sm text-center max-w-xs mb-8" data-testid="error-boundary-message">
            {message}
          </p>
          {scoped ? (
            <button
              onClick={this.handleRetry}
              data-testid="error-boundary-retry-btn"
              className="w-full max-w-xs h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-base rounded-2xl shadow-lg shadow-amber-500/25 active:scale-[0.98] transition-all"
            >
              Reintentar
            </button>
          ) : (
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-refresh-btn"
              className="w-full max-w-xs h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-base rounded-2xl shadow-lg shadow-amber-500/25 active:scale-[0.98] transition-all"
            >
              Recargar
            </button>
          )}
          <p className="text-stone-400 text-xs mt-8">
            Si el problema persiste, contacta soporte
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
